// AP Payment Request (per-provider approval container) — mirrors Finance.API ApPaymentRequestDto.
import { Currency, EntityKey } from '../ap-document/ap-document';
import { Translation } from '../Translations';

export enum ApPaymentRequestStatus {
  Draft = 1,
  PendingApproval = 2,
  Approved = 3,
  Rejected = 4,
  Cancelled = 5,
}

export interface ApPaymentRequestLine {
  apDocumentReference: string;
  /** The AP-generated document number. */
  invoiceNumber: string;
  /** The provider's own printed/fiscal number, snapshotted onto the line at selection time (#4788) so a
   * settled document still renders it. Empty when the document carries no fiscal number. */
  fiscalNumber?: string;
  /** Total settled against the invoice (cash + advance). Cash actually paid = amountToPay − advanceAppliedAmount. */
  amountToPay: number;
  /** Withholding for this invoice (#4304), proportional to amountToPay. Server-computed, read-only. */
  withholdingAmount: number;
  /** Vendor Advance (Anticipo) the requester earmarked to draw down against this document (#4349). */
  advanceAppliedAmount?: number;
  /** Why this line pays something other than what is owed (#4787). */
  note?: string | null;
  /** AP control account the line's document posted to, snapshotted at selection time (#4797). Always
   * present — a request only accepts posted documents, and no AP document can be registered without a
   * control account. Per line, not per request: the account is pinned on the document at registration, so
   * one provider's invoices can post to different accounts and a request can legitimately span several. */
  apAccountReference: string;
  apAccountNumber: string;
  /** Localized account name. Genuinely optional, unlike the reference and number — nothing enforces a name
   * on the document. */
  apAccountName?: Translation | null;
  /** The document's currency — the one `amountToPay` is expressed in. Always sent: it is `required` on the
   * API line, because USD is the enum's zero value and a fallback would read an HNL amount as USD. */
  currency: Currency;
  /** Functional (reporting) currency of `functionalAmount`. USD in practice, but sent explicitly so a
   * USD-only consumer can assert rather than assume. */
  functionalCurrency: Currency;
  /** Currency → functional multiplier, copied from the document: the rate the GL posted it with. */
  exchangeRate: number;
  /** `amountToPay` converted at `exchangeRate` — what a USD cash request plans with. Not the document's
   * own functional total, which covers the whole invoice. */
  functionalAmount: number;
}

export interface ProviderBankingDetails {
  accountNumber?: string | null;
  swiftBicCode?: string | null;
  ibanCode?: string | null;
  bankName?: string | null;
  bankAddress?: string | null;
}

export interface ProviderBankingSnapshot {
  paymentBank?: ProviderBankingDetails | null;
  intermediateBank?: ProviderBankingDetails | null;
}

export interface ApPaymentRequest {
  id?: string | null;
  number: string;
  financialCompanyReference: string;
  providerReference: string;
  providerCode: string;
  providerLegalName: string;
  selectedProviderBankId?: string | null;
  /** The ONE currency every line is in — null when the request mixes them (#5135). A request has no
   *  currency of its own; the lines carry theirs. */
  currency?: Currency | null;
  status: ApPaymentRequestStatus;
  /** What the request comes to in its own currency — null when it has none, because the lines mix
   *  currencies and no such figure exists. Use `totalFunctionalAmount` then. */
  totalAmount?: number | null;
  /** What the request comes to in the functional currency, summed from each line's own functional amount.
   *  The only total a mixed request has, and always safe to show. */
  totalFunctionalAmount: number;
  /** The currency `totalFunctionalAmount` is stated in (USD in practice). */
  functionalCurrency: Currency;
  /** True when the lines are not all in one currency. */
  isMixedCurrency: boolean;
  /** Total withholding across the requested invoices (#4304). Net to provider = totalAmount − this. */
  totalWithholding: number;
  lines: ApPaymentRequestLine[];
  banking?: ProviderBankingSnapshot | null;
  spawnedApPaymentReference?: string | null;
  /** Cash requests this request has been planned into (#4798). Many-to-many — a request can be funded
   * across more than one week. Maintained by the cash request; read-only here. */
  cashRequestReferences?: EntityKey[] | null;
  notes?: string | null;
  createdBy?: string;
  createdDate?: string;
  modifiedBy?: string;
  modifiedDate?: string;
}

export interface ApPaymentRequestLineInsert {
  apDocumentReference: string;
  /** Total to settle against the invoice (cash + advance). */
  amountToPay: number;
  /** Vendor Advance (Anticipo) to earmark against this document (#4349). Default 0. */
  advanceAppliedAmount?: number;
  /** Why this line pays something other than what is owed (#4787). Must be sent on every save — the API
   * rebuilds the request's lines from this payload, so omitting it destroys the note. */
  note?: string | null;
}

export interface ApPaymentRequestInsert {
  /** API resolves the real value from the current site; the UI value is ignored. */
  financialCompanyReference: string;
  providerReference: string;
  selectedProviderBankId?: string | null;
  notes?: string | null;
  lines: ApPaymentRequestLineInsert[];
}

/** Approver edit payload — everything except the provider (locked at creation). */
export interface ApPaymentRequestUpdate {
  selectedProviderBankId?: string | null;
  notes?: string | null;
  lines: ApPaymentRequestLineInsert[];
}

export interface ApPaymentRequestSearchCommand {
  financialCompanyReference?: string | null;
  providerReference?: string | null;
  number?: string | null;
  /** Any line's document — our AP number or the provider's fiscal number (same box as the AP payments list). */
  invoiceNumber?: string | null;
  status?: ApPaymentRequestStatus | null;
}
